/**
 * listen.js — a document listener that survives a lost race.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE BUG THIS EXISTS TO PREVENT
 *
 * Firestore's onSnapshot reports an error ONCE and then gives up. It does not
 * retry, and there is no way to ask it to. So a listener that fails for a
 * moment stays failed for the life of the component.
 *
 * On its own that would be a visible fault. What made it insidious here is how
 * the "am I in this state?" hooks handled it:
 *
 *     onSnapshot(ref, (snap) => setVoted(snap.exists()), () => setVoted(false))
 *
 * The error handler renders the failure as a FACT. Not "unknown", not "still
 * loading" — a confident "no, you have not voted", "no, you have not reposted",
 * "no, you have not reacted". The UI then offers to do the thing again, the
 * user does, and the write is rejected or duplicated.
 *
 * The permission error that triggers it is usually a race rather than a rule.
 * Straight after a sign-in or an account switch, React re-renders with the new
 * uid and re-subscribes before the SDK has swapped the auth token, so the
 * listener asks for account B's document holding account A's credentials. Fifty
 * milliseconds of mismatch, and the answer is wrong until the page reloads.
 *
 * Symptoms this produced, all of which read as separate bugs:
 *   - a poll vote that saved, then displayed as though it never happened
 *   - "Undo repost" never appearing, so undo appeared not to work at all
 *   - reactions silently reverting to unreacted
 *
 * So: refresh the token, re-subscribe, back off, and only report failure once
 * the retries are genuinely spent.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { onSnapshot } from 'firebase/firestore';
import { auth } from '../firebase';

/**
 * @param ref              a Firestore DocumentReference or Query
 * @param onData           snapshot handler
 * @param opts.onFail      called only after the retries are exhausted
 * @param opts.retries     how many times to re-subscribe (default 3)
 * @returns unsubscribe
 */
export function listen(ref, onData, { onFail, retries = 3 } = {}) {
  let cancelled = false;
  let unsub = null;
  let timer = null;
  let attempt = 0;

  const start = () => {
    if (cancelled) return;

    unsub = onSnapshot(
      ref,
      (snap) => {
        // A good snapshot restores the budget, so a listener open for hours
        // does not eventually exhaust it on unrelated blips.
        attempt = 0;
        onData(snap);
      },
      (err) => {
        const racy = err?.code === 'permission-denied' || err?.code === 'unauthenticated';

        if (racy && attempt < retries && !cancelled) {
          attempt += 1;
          timer = setTimeout(async () => {
            // Force a fresh token before trying again — if the cause was a
            // stale token, re-subscribing with the same one changes nothing.
            try { await auth.currentUser?.getIdToken(true); } catch { /* offline */ }
            if (cancelled) return;
            unsub?.();
            start();
          }, 700 * attempt);
          return;
        }

        console.warn('Listener failed', ref?.path ?? '(query)', err?.code);
        onFail?.(err);
      }
    );
  };

  start();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    unsub?.();
  };
}
